import React, { useState } from 'react';
import { X, Clock, RefreshCw, FileText, Brain, Target, MessageCircle, TrendingUp, ListChecks, Search, UserCheck, CalendarCheck, BarChart3, Flame, Sun, Snowflake, UserPlus, Send, Shield } from 'lucide-react';

interface LinkedInHowIWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'process' | 'behavior' | 'intelligence';

const processSteps = [
  {
    icon: Search,
    title: 'Receive & Research Leads',
    description: 'I receive your approved leads and research each prospect\'s LinkedIn profile — their role, company, mutual connections, shared interests, and recent posts — to craft a meaningful connection approach.',
  },
  {
    icon: UserPlus,
    title: 'Send Connection Request',
    description: 'I send a personalized connection note that references something specific about the prospect — their industry, a shared interest, or a recent achievement — to maximize acceptance rates.',
  },
  {
    icon: MessageCircle,
    title: 'Send Intro Message',
    description: 'Once the connection is accepted, I send a warm welcome message that builds rapport naturally — no hard sell, just a genuine conversation starter that opens the door for further dialogue.',
  },
  {
    icon: Send,
    title: 'Deliver Pitch Message',
    description: 'After establishing rapport, I share your value proposition in a conversational way, tying it directly to the prospect\'s role, industry challenges, or stated goals on their profile.',
  },
  {
    icon: RefreshCw,
    title: 'Follow Up Intelligently',
    description: 'If the prospect goes quiet, I send up to 3 thoughtful follow-ups with different angles — social proof, a relevant insight, or a low-commitment ask — spaced at optimal intervals.',
  },
  {
    icon: CalendarCheck,
    title: 'Book Meeting or Transition Channel',
    description: 'When the prospect shows interest, I move to book a meeting. If they prefer another channel, I facilitate the transition to email or phone seamlessly.',
  },
  {
    icon: BarChart3,
    title: 'Log & Analyze Results',
    description: 'Every interaction is logged with details — connection status, message engagement, reply sentiment, and conversation summary — so you have full visibility into your LinkedIn pipeline.',
  },
];

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'process', label: 'Process', icon: ListChecks },
  { id: 'behavior', label: 'Behavior', icon: Target },
  { id: 'intelligence', label: 'Intelligence', icon: Brain },
];

const LinkedInHowIWorkModal: React.FC<LinkedInHowIWorkModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('process');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg flex-shrink-0">
              <Brain className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-2xl font-extrabold text-gray-900 font-display tracking-tight">How I Work</h3>
              <p className="text-sm text-gray-600 mt-1">LinkedIn outreach process, behavior rules and intelligence</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-gray-50">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 font-semibold transition-all duration-300 relative ${
                  activeTab === tab.id
                    ? 'text-[#1875FF] bg-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center justify-center space-x-2">
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </span>
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'process' && <ProcessTab />}
          {activeTab === 'behavior' && <BehaviorTab />}
          {activeTab === 'intelligence' && <IntelligenceTab />}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-semibold hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

const ProcessTab = () => (
  <div className="space-y-4 animate-in slide-in-from-left duration-300">
    {processSteps.map((step, index) => {
      const Icon = step.icon;
      return (
        <div
          key={index}
          className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 hover:border-[#1875FF]/30 transition-all hover:shadow-lg"
        >
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 self-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg">
                <Icon className="text-white" size={24} />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-gray-400 mb-1">
                STEP {index + 1}: <span className="text-gray-900">{step.title}</span>
              </h4>
              <p className="text-gray-700 leading-relaxed">{step.description}</p>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const BehaviorTab = () => (
  <div className="space-y-6 animate-in slide-in-from-left duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Shield className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Activity Limits</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I stay within LinkedIn's safe engagement thresholds to protect your account:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Daily connection request limit — configurable per account</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Daily message limit — controlled to avoid restrictions</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Daily profile lookup limit — natural browsing patterns maintained</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Gradual ramp-up for new accounts to build activity history safely</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <RefreshCw className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Outreach Sequence Logic</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I follow a 6-step LinkedIn outreach sequence:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">1.</span>
              <span>Connection request — personalized note referencing their profile</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">2.</span>
              <span>Intro message — warm welcome after connection is accepted</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">3.</span>
              <span>Pitch — value proposition tied to their specific role and needs</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">4.</span>
              <span>Follow-up 1 — new angle with social proof or a relevant insight</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">5.</span>
              <span>Follow-up 2 — lightweight touchpoint, easy to reply to</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">6.</span>
              <span>Follow-up 3 — final message, soft close or break-up</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&bull;</span>
              <span>Configurable delay between each step (default: days apart)</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <UserCheck className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Profile Engagement Strategy</h4>
          <p className="text-gray-700 leading-relaxed">
            Before sending a connection request, I visit the prospect's profile to create natural
            activity. This mimics genuine interest and increases connection acceptance rates.
            I also engage with relevant content to build familiarity before the first message.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Clock className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Persona & Tone</h4>
          <p className="text-gray-700 leading-relaxed">
            I adapt my messaging tone and persona based on your configuration — professional, casual,
            or consultative. Every message sounds authentically human, matches your brand voice, and
            feels like a natural LinkedIn conversation, not a sales blast.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">When I Stop Messaging</h4>
          <p className="text-gray-700 leading-relaxed">
            I stop messaging when: the connection request is rejected or withdrawn, the prospect explicitly
            declines, the full 6-step sequence is completed, a meeting has been booked, or the lead is
            marked as unqualified by your team.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const IntelligenceTab = () => (
  <div className="space-y-6 animate-in slide-in-from-right duration-300">
    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <UserPlus className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Reply & Acceptance Detection</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I track and analyze key engagement metrics across the LinkedIn funnel:
          </p>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Connection acceptance rate:</strong> How quickly and often prospects accept</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Message reply rate:</strong> Which messages generate the most responses</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Response time patterns:</strong> When prospects are most likely to reply</span>
            </li>
            <li className="flex items-start">
              <span className="text-[#1875FF] mr-2">&rarr;</span>
              <span><strong>Engagement depth:</strong> Multi-reply conversations vs. single responses</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <TrendingUp className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Temperature Classification</h4>
          <p className="text-gray-700 leading-relaxed mb-3">
            I classify leads based on LinkedIn engagement signals:
          </p>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 border border-red-400/20 shadow-sm shadow-red-500/10">
              <Flame size={14} className="text-red-500" />
              Hot Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-500/8 text-amber-600 border border-amber-500/15 shadow-sm">
              <Sun size={14} />
              Warm Lead
            </span>
            <span className="text-gray-300 font-light">|</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-[#1875FF]/8 text-[#1875FF] border border-[#1875FF]/15 shadow-sm">
              <Snowflake size={14} />
              Cold Lead
            </span>
          </div>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-6 border border-[#1875FF]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <FileText className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Conversation Summarization</h4>
          <p className="text-gray-700 leading-relaxed">
            After each LinkedIn conversation, I create structured summaries extracting key information —
            prospect interest level, topics discussed, objections raised, and agreed next steps. You get
            full pipeline context without reading every message thread.
          </p>
        </div>
      </div>
    </div>

    <div className="bg-gradient-to-br from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-6 border border-[#05E0D1]/20">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
          <Brain className="text-white" size={24} />
        </div>
        <div className="flex-1">
          <h4 className="font-extrabold text-gray-900 text-lg mb-2 font-display">Pattern Learning</h4>
          <p className="text-gray-700 leading-relaxed">
            I continuously learn which connection note styles get the highest acceptance rates,
            which pitch angles generate the most replies, and what follow-up timing converts best.
            These insights are applied to optimize your LinkedIn outreach automatically.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default LinkedInHowIWorkModal;
